import { Menu, MenuDivider, MenuItem, Position } from "@blueprintjs/core"
import { Prohibit } from "@phosphor-icons/react"
import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import CustomPopover from "components/Atoms/CustomPopover"
import NewTag from "components/Atoms/NewTag"
import StaticSelector from "components/Atoms/StaticSelector"
import StatusIcon from "components/Atoms/StatusIcon"
import StyledLink from "components/Atoms/StyledLink"
import AlertDialogue from "components/Common/AlertDialogue"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import NewResourceButton from "components/Organisms/AddResourceButton"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import SerialColumn from "components/Organisms/table/SerialColumn"
import {
  useAllGroupsSelectorQuery,
  useAllSchedulingOfficersQuery,
} from "generated/graphql"
// Queries and Mutations
import { DELETE_USER, SYSTEM_USER_LIST } from "graphql/system-user.gql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import ReactTable from "react-table"
const Users = () => {
  const navigate = useNavigate()
  const [alertVisible, changeAlert] = useState(false)
  const [id, changeId] = useState(null)
  const [{ page, search, group, next }, setFilter] = useSearchFilter({
    group: "*",
  })

  const COLUMNS = [
    SerialColumn,
    {
      accessor: "actions",
      width: 50,
      Cell({ original, index }) {
        return (
          <CustomPopover
            position={Position.BOTTOM_LEFT}
            minimal={true}
            content={
              <Menu>
                <MenuItem
                  id={`edit_user_${index}`}
                  text="Edit User"
                  icon="edit"
                  onClick={e => navigate(original.id)}
                />
                <MenuDivider />
                <MenuItem
                  icon="projects"
                  text="Archive User"
                  onClick={e => handleDelete(original.id)}
                />
              </Menu>
            }
          >
            <CustomButton
              id={`edit_user_button_${index}`}
              style={{ transform: "rotate(90deg)" }}
              icon="more"
              minimal={true}
            />
          </CustomPopover>
        )
      },
    },
    {
      Header: "Status",
      accessor: "status",
      width: 75,
      Cell: ({ original }) => {
        return (
          <StatusIcon
            values={{
              AV: { intent: "success", text: "AV", tooltip: "Available" },
              NA: { intent: "danger", text: "NA", tooltip: "Not Available" },
              OL: { intent: "warning", text: "OL", tooltip: "On Leave" },
              BS: { intent: "none", text: "BS", tooltip: "Busy" },
            }}
            condition={original.status}
          ></StatusIcon>
        )
      },
    },
    {
      Header: "Name",
      accessor: "fullName",
      Cell({ original, value }) {
        return (
          <>
            <StyledLink
              className={`${original?.isBlocked ? "text-red-500" : ""}`}
              to={original.id}
            >
              <div className="flex items-center gap-2 font-500">
              {value}{" "}
              {
                original?.isBlocked && (
                  <Prohibit weight="fill" size={20} color="red" />
                )
              }

              </div>
            
              

            </StyledLink>
            <NewTag createdAt={original.createdAt} />
          </>
        )
      },
    },
    {
      Header: "Mobile Number",
      accessor: "number",
    },
    {
      Header: "Departments",
      accessor: "departments.name",
      width: 350,
      Cell({ original }) {
        return (
          <OverFlowMenu
            intent="primary"
            icon="briefcase"
            onClick={item => navigate(`/d/registration/department/${item.id}`)}
            items={original.departments}
          ></OverFlowMenu>
        )
      },
    },
    {
      Header: "Groups",
      accessor: "groups.name",

      Cell({ original }) {
        return (
          <OverFlowMenu
            intent="primary"
            icon="people"
            items={original.groups}
          ></OverFlowMenu>
        )
      },
    },
  ]

  const handleDelete = id => {
    changeAlert(true)
    changeId(id)
  }

  const {
    data: uData,
    loading,
    error,
  } = useAllSchedulingOfficersQuery({
    onCompleted({ usersByGroup }) {
      if (usersByGroup?.length === 0) {
        setFilter(prev => {
          return { ...prev, next: false }
        })
      } else {
        setFilter(prev => {
          return { ...prev, next: true }
        })
      }
    },
    variables: {
      query: search ?? "",
      first: 13,
      skip: page * 13,
      groupName: group,
    },
  })
  const list = uData?.usersByGroup ?? []
  const { data, loading: loadingGroups } = useAllGroupsSelectorQuery()
  if (error) return <>{`Errors ${error.message}`}</>

  return (
    <CustomCard className="p-0">
      <ReactTableHeader
        icon="user"
        title="User"
        value={search ?? ""}
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
      >
        <StaticSelector
          loading={loadingGroups}
          items={[
            {
              id: "*",
              name: "All Groups",
            },
          ].concat(data?.allGroups)}
          formGroupProps={{
            className: "mb-0",
          }}
          className="mb-0 mr-3"
          big={false}
          valueAs="object"
          onChange={(_, value) => {
            setFilter(prev => ({
              ...prev,
              group: value.name === "All Groups" ? "*" : value.name,
            }))
          }}
          renderText={() => (group === "*" ? "All Group" : group)}
          path="group"
        />
        <TablePagination
          disabledNext={next === "false"}
          className="mx-3"
          page={page}
          onPageChange={page => {
            setFilter(prev => ({
              ...prev,
              page,
            }))
          }}
        />

        <NewResourceButton module="user" />
      </ReactTableHeader>

      <ReactTable
        className="mt-0 vt-master-table vt_user_table"
        loading={loading}
        pageSize={13}
        showPagination={false}
        resizable={true}
        data={list}
        columns={COLUMNS}
        getTrProps={(_, rowInfo) => {
          if (rowInfo && rowInfo.original) {
            return rowInfo.original?.isBlocked
              ? {
                  className: "bg-red-50",
                }
              : {}
          }
          return {}
        }}
      />

      <AlertDialogue
        alertFor="User"
        isOpen={alertVisible}
        onClose={value => changeAlert(value)}
        mutation={DELETE_USER}
        refetchQueries={[
          {
            query: SYSTEM_USER_LIST,
            variables: {
              query: "",
              groupName: "Scheduling Officer",
              skip: 0,
              first: 13,
            },
          },
        ]}
        variables={{
          id,
        }}
      />
    </CustomCard>
  )
}
export default Users
